import { useState, useEffect, useRef } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FormField from "layouts/applications/wizard/components/FormField";
import { useNavigate, useParams } from "react-router-dom";

import CrudService from "services/cruds-service";
import MDEditor from "components/MDEditor";
import { Popover, List, ListItem, ListItemText } from "@mui/material";
import { PROMPT_TEMPLATE_VARIABLES } from "utils/constant";
import Scrollbar from "react-perfect-scrollbar"; // Import Scrollbar component
import { useMaterialUIController } from "context";

const EditPromptTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [messageData, setMessageData] = useState({});
  const [message, setMessage] = useState({
    id: "",
    name: "",
    content: "",
  });

  const [error, setError] = useState({
    name: "",
    content: "",
  });

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [tempName, setName] = useState("");
  const [tempContent, setContent] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);

  const quillRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [variables, setVariables] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await CrudService.getAllPromptTag();
        setVariables(response.data);
      } catch (err) {
        console.error(err);
        return null;
      }
    })();

  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await CrudService.getPromptTemplate(id);
        setMessageData(res.data);
        setMessage({
          id: res.data.id,
          name: res.data?.name,
          content: res.data?.content,
        });
        setName(res.data?.name);
        setContent(res.data?.content);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    // Listen for keydown event to detect Shift + #
    const handleKeyDown = (e) => {
      if (e.key == "F2") {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const selection = quill.getSelection();
          if (selection) {
            const cursorBounds = quill.getBounds(selection.index);
            setCursorPosition(selection.index);
            setPopoverPosition({
              top: cursorBounds.top, // Top position of the cursor
              left: cursorBounds.left, // Left position of the cursor
            });
            setAnchorEl(document.activeElement); // Show the popover
          }
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup event listener
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();

    // const formattedCompany = company.map((element) => element.attributes);
    let name = "";
    let content = "";
    if (
      message.name == undefined ||
      message.name == "" ||
      message.content == undefined ||
      message.content == "" ||
      message.id == undefined ||
      message.id == ""
    ) {
      name = tempName;
      content = tempContent;
    } else {
      name = message.name;
      content = tempContent;
    }
    const updateMessage = {
      name: name,
      content: content,
    };

    try {
      await CrudService.updatePromptTemplate(id, updateMessage);
      navigate("/prompt-template", {
        state: {
          value: true,
          text: "The Prompt template was successfully upated",
        },
      });
    } catch (err) {
      if (err.hasOwnProperty("msg")) {
        setError({
          ...error,
          error: true,
          textError: err.msg,
        });
      }
      console.error(err);
    }
  };

  const changeNameHandler = (e) => {
    setMessage({ ...message, name: e.target.value });
    setName(e.target.value);
  };

  const handleEditorChange = (value) => {
    setContent(value);
  };

  const handleOnKeyUp = (value) => {
    if(value.code == 'F2'){
      setAnchorEl(document.activeElement);
      return;
    }
  }

  const handleVariableInsertion = (variable) => {
    const quill = quillRef.current?.getEditor();
    if (quill && cursorPosition !== null) {
      quill.insertText(cursorPosition, `{{${variable}}}`);
      quill.setSelection(cursorPosition + `{{${variable}}}`.length);
    }
    setAnchorEl(null);
  };

  const handleKeyDown = (event) => {
    if (variables.length === 0) return;

    if (event.key === 'ArrowDown') {
      setSelectedIndex((prevIndex) => (prevIndex === null ? 0 : Math.min(prevIndex + 1, variables.length - 1)));
    } else if (event.key === 'ArrowUp') {
      setSelectedIndex((prevIndex) => (prevIndex === null ? 0 : Math.max(prevIndex - 1, 0)));
    } else if (event.key === 'Enter' && selectedIndex !== null) {
      handleVariableInsertion(variables[selectedIndex].display_field); // Handle selection
      setAnchorEl(null); // Close the popover
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={0} mb={2} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Edit Template {id}
                </MDTypography>
              </MDBox>
            </MDBox>
            <Card>
              <MDBox component="form" method="POST" onSubmit={submitHandler}>
                <MDBox display="flex" flexDirection="column" px={3} my={2}>
                  <TextField
                    type="text"
                    label="Name"
                    name="name"
                    variant="standard"
                    // defaultValue={message.name}
                    value={message.name}
                    onChange={changeNameHandler}
                    error={error.name}
                    InputLabelProps={{ shrink: true }}
                  />

                  <MDBox mt={2}>
                    <MDBox
                      mb={1}
                      ml={0.5}
                      lineHeight={0}
                      display="inline-block"
                    >
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="regular"
                        color="text"
                      >
                        Template Content&nbsp;&nbsp;
                      </MDTypography>
                    </MDBox>
                    <MDEditor
                      value={tempContent}
                      onChange={handleEditorChange}
                      ref={quillRef}
                      onKeyUp={handleOnKeyUp}
                    />
                    <Popover
                      open={Boolean(anchorEl)}
                      anchorEl={anchorEl}
                      onClose={() => setAnchorEl(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      style={{
                        position: "absolute",
                        top: popoverPosition.top + window.scrollY - 210,
                        left: popoverPosition.left + window.scrollX - 10,
                      }}
                      onKeyDown={handleKeyDown}
                    >
                      <Scrollbar>
                        <List
                          style={{
                            backgroundColor: !darkMode ? "white" : "black",
                            color: darkMode ? "white" : "black",
                            overflowY: "scroll",
                            maxHeight: "200px",
                          }}
                        >
                          {variables.map((variable, index) => (
                            <ListItem
                              key={index}
                              selected={selectedIndex === index}
                              onClick={() => handleVariableInsertion(variable.display_field)}
                            >
                              <ListItemText>
                                <MDTypography variant="body2">
                                  {variable.display_field}
                                </MDTypography>
                              </ListItemText>
                            </ListItem>
                          ))}
                        </List>
                      </Scrollbar>
                    </Popover>
                  </MDBox>

                  <MDBox
                    ml="auto"
                    mt={4}
                    mb={2}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <MDBox mx={2}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate("/prompt-template", {
                            state: {
                              value: false,
                              text: "",
                            },
                          })
                        }
                      >
                        Back
                      </MDButton>
                    </MDBox>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      type="submit"
                    >
                      Update
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default EditPromptTemplate;
